// src/components/CoverImageSelector/index.jsx
import { PictureOutlined, UploadOutlined } from '@ant-design/icons';
import React, { useState, useCallback, useMemo, lazy, Suspense, useEffect } from 'react';
import { Segmented, theme, Flex, Space, Spin } from 'antd';
import { Dropdown } from './../../Dropdown';
import clientService from 'cms/src/apiClient';

const GALLERY = 'gallery';
const UPLOAD = 'upload';

const { useToken } = theme;
const GallerySegment = lazy(() => import('./GallerySegment'));
const UploadSegment = lazy(() => import('./UploadSegment'));

export const CoverImageSelector = React.memo(({ onClick, fileId, workspaceId, imageType = 'cover' }) => {
  const [activeSegment, setActiveSegment] = useState(GALLERY);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { token } = useToken();

  const options = useMemo(
    () => [
      { icon: <PictureOutlined />, label: 'Gallery', value: GALLERY },
      { icon: <UploadOutlined />, label: 'Upload', value: UPLOAD },
    ],
    []
  );

  // Fetch gallery items when component mounts or when switching to gallery
  useEffect(() => {
    const fetchGalleryItems = async () => {
      setLoading(true);
      try {
        const endpoint = `/onedoc_backend/v1/thumbnails?type=${imageType ?? 'cover'}`;
        const response = await clientService.get(endpoint);
        if (response?.status_code === 200) {
          const items = transformGalleryData(response?.data);
          setGalleryItems(items);
        }
      } catch (error) {
        console.error('Failed to fetch gallery items:', error);
        setGalleryItems([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    if (activeSegment === GALLERY) {
      fetchGalleryItems();
    }
  }, [activeSegment, imageType]);

  const transformGalleryData = (data) => {
    const items = [];
    const categoryNameMap = {
      abstract: 'Abstract',
      solid: 'Solid Colors',
      gradient: 'Gradients',
      custom: 'Custom',
    };
    if (Array.isArray(data?.images)) {
      data.images.forEach((image) => {
        const key = categoryNameMap[image.category] || image.category || 'Gallery';

        items.push({
          key,
          src: image.url,
          id: image.id,
          s3_key: image.s3_key,
          category: image.category,
          type: image.type,
        });
      });

      return items;
    }

    if (data && typeof data === 'object') {
      Object.entries(data).forEach(([category, images]) => {
        images?.forEach((image) => {
          items.push({
            key: categoryNameMap[category] || category,
            src: image.url,
            id: image.id,
            s3_key: image.s3_key,
            category,
          });
        });
      });
    }

    return items;
  };

  const onSegmentChange = useCallback((value) => setActiveSegment(value), []);

  return (
    <Flex vertical style={{ padding: token.paddingSM }}>
      <Flex style={{ padding: 0, border: 'none', marginBottom: token.marginXS }} className="dropdown__header">
        <Segmented value={activeSegment} options={options} onChange={onSegmentChange} />
      </Flex>
      <Space
        style={{
          padding: 0,
          minHeight: '200px',
          width: '100%',
          minWidth: '476px',
        }}
        className="dropdown__body"
      >
        {loading ? (
          <Flex justify="center" align="center" style={{ minHeight: 200, minWidth: '476px', width: '100%' }}>
            <Spin />
          </Flex>
        ) : (
          <Suspense fallback={<Spin />}>
            {activeSegment === GALLERY && <GallerySegment items={galleryItems} onClick={onClick} />}
            {activeSegment === UPLOAD && (
              <UploadSegment onClick={onClick} fileId={fileId} workspaceId={workspaceId} imageType={imageType} />
            )}
          </Suspense>
        )}
      </Space>
    </Flex>
  );
});

export const CoverSelectorButton = React.memo(
  ({ children, open, onOpenChange, onClick, items, fileId, workspaceId, imageType = 'thumbnail' }) => {
    // Note: items prop is kept for backward compatibility but will be fetched from backend
    return (
      <Dropdown
        overlayClassName="cover-image__dropdown"
        content={
          <CoverImageSelector onClick={onClick} fileId={fileId} workspaceId={workspaceId} imageType={imageType} />
        }
        open={open}
        onOpenChange={onOpenChange}
        placement="bottomLeft"
      >
        {children}
      </Dropdown>
    );
  }
);

export default CoverSelectorButton;
